type StorageKind = 'localStorage' | 'sessionStorage';
type ConsentWindow = Window & {
    cookie_consent?: unknown;
};

const createMemoryStorage = (): Storage => {
    const store = new Map<string, string>();

    return {
        get length() {
            return store.size;
        },
        clear: () => store.clear(),
        getItem: (key: string) => store.get(key) ?? null,
        key: (index: number) => Array.from(store.keys())[index] ?? null,
        removeItem: (key: string) => {
            store.delete(key);
        },
        setItem: (key: string, value: string) => {
            store.set(key, value);
        },
    };
};

const memoryStorage: Record<StorageKind, Storage> = {
    localStorage: createMemoryStorage(),
    sessionStorage: createMemoryStorage(),
};

const unavailableStorage: Storage = {
    get length() {
        return 0;
    },
    clear: () => {},
    getItem: () => null,
    key: () => null,
    removeItem: () => {},
    setItem: () => {},
};

const getBrowserStorage = (kind: StorageKind): Storage | null => {
    if (typeof window === 'undefined') return null;

    try {
        const consent = (window as ConsentWindow).cookie_consent;
        if (
            consent &&
            typeof consent === 'object' &&
            ((consent as Record<string, unknown>).STORAGE === 'DENY' ||
                (consent as Record<string, unknown>).storage === 'DENY')
        ) {
            return null;
        }

        const storage = window[kind];
        const testKey = '__maap_storage_test__';

        storage.setItem(testKey, testKey);
        storage.removeItem(testKey);

        return storage;
    } catch {
        return null;
    }
};

const browserStorageCache: Partial<Record<StorageKind, Storage>> = {};

export const getSafeStorage = (kind: StorageKind): Storage => {
    if (typeof window === 'undefined') return unavailableStorage;

    const cached = browserStorageCache[kind];
    if (cached) return cached;

    const browserStorage = getBrowserStorage(kind);
    if (browserStorage) {
        browserStorageCache[kind] = browserStorage;
        return browserStorage;
    }

    return memoryStorage[kind];
};

export const safeLocalStorage = {
    getItem: (key: string): string | null =>
        getSafeStorage('localStorage').getItem(key),
    removeItem: (key: string): void => {
        getSafeStorage('localStorage').removeItem(key);
    },
    setItem: (key: string, value: string): void => {
        getSafeStorage('localStorage').setItem(key, value);
    },
};

export const safeSessionStorage = {
    getItem: (key: string): string | null =>
        getSafeStorage('sessionStorage').getItem(key),
    removeItem: (key: string): void => {
        getSafeStorage('sessionStorage').removeItem(key);
    },
    setItem: (key: string, value: string): void => {
        getSafeStorage('sessionStorage').setItem(key, value);
    },
};
